import {
  partDisplayType,
  type ThreadPartLike,
} from '@/components/agent-chat/question-card.util';
import { canonicalToolName } from '@/components/agent-chat/tool-labels';

/**
 * What Clu learns (CLE-562): the display of a `propose_learning` result, which
 * draws as one row under Clu's reply (see LearningRow).
 */
export const LEARNING_DISPLAY = 'learning';

/** Clu's tools for what it learns and the skills it reads. */
const MEMORY_TOOLS: ReadonlySet<string> = new Set([
  'propose_learning',
  'read_skill',
]);

const STATES = [
  'saved',
  'needs_review',
  'sent_for_review',
  'not_saved',
] as const;
// evidence: a change to an agent's instructions from its outcomes (P2-2).
// voice: a Company voice setting of Clu writing; proof: a customer result.
const KINDS = [
  'note',
  'skill',
  'instructions',
  'evidence',
  'voice',
  'proof',
] as const;
const SCOPES = ['company', 'personal', 'agent'] as const;

/** The result's display, as `propose_learning` writes it. */
export interface LearningDisplay {
  learningId: string;
  state: (typeof STATES)[number];
  kind: (typeof KINDS)[number];
  scope: (typeof SCOPES)[number];
  label: string;
  /** The save can be undone by this caller. */
  canUndo: boolean;
  /** This caller may approve or decline it. */
  canReview: boolean;
}

function oneOf<T extends string>(
  values: readonly T[],
  value: unknown
): T | null {
  return values.find((candidate) => candidate === value) ?? null;
}

/** The display as a learning, or null when a field is missing or unknown. */
export function parseLearning(display: unknown): LearningDisplay | null {
  if (!display || typeof display !== 'object') return null;
  const data = display as Record<string, unknown>;
  const state = oneOf(STATES, data.state);
  const kind = oneOf(KINDS, data.kind);
  const scope = oneOf(SCOPES, data.scope);
  const label = typeof data.label === 'string' ? data.label.trim() : '';
  if (
    !state ||
    !kind ||
    !scope ||
    !label ||
    typeof data.learningId !== 'string' ||
    !data.learningId
  ) {
    return null;
  }
  return {
    learningId: data.learningId,
    state,
    kind,
    scope,
    // A voice label names the setting (`max_words`), a proof's the customer.
    label:
      kind === 'voice'
        ? 'Company voice'
        : kind === 'proof'
          ? `Customer proof · ${label}`
          : label,
    canUndo: data.canUndo === true,
    canReview: data.canReview === true,
  };
}

/** The row's words, or null for a save no row is written for. */
export function learningRowText({
  state,
  kind,
  scope,
  label,
}: LearningDisplay): string | null {
  switch (state) {
    case 'saved':
      if (kind === 'skill') return `Saved skill · ${label}`;
      if (scope === 'company') return `Saved for everyone · ${label}`;
      if (scope === 'personal') return `Saved for you · ${label}`;
      return null;
    case 'needs_review':
      return `Needs review · ${label}`;
    case 'sent_for_review':
      return `Sent for review · ${label}`;
    case 'not_saved':
      return `Not saved · ${label}`;
  }
}

/** A tool of Clu's memory: `propose_learning` or `read_skill`, by any alias. */
export const isMemoryTool = (name: unknown): boolean =>
  typeof name === 'string' && MEMORY_TOOLS.has(canonicalToolName(name));

function isMemoryPart(part: ThreadPartLike): boolean {
  if (part.type !== 'tool-call' || part.toolName !== 'tool_result') {
    return false;
  }
  if (partDisplayType(part) === LEARNING_DISPLAY) return true;
  return isMemoryTool((part.args as { name?: unknown } | undefined)?.name);
}

/**
 * A message's parts without the memory tools' (a call still running, its
 * result, any learning display), for a chat whose flag is off or still
 * loading. Each becomes a part of no kind, so it takes no step, no count and
 * no card, and every other part keeps its index.
 */
export function hideMemoryParts(
  parts: readonly ThreadPartLike[]
): readonly ThreadPartLike[] {
  return parts.some(isMemoryPart)
    ? parts.map((part) => (isMemoryPart(part) ? { type: 'hidden' } : part))
    : parts;
}
